import { Suspense, lazy, useEffect } from 'react';
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { ChromeProvider, useChromeState } from "@/contexts/ChromeContext";
import { Navbar } from "@/components/layout/Navbar";
import { Sidebar } from "@/components/layout/Sidebar";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { CookieBanner, CookieSettingsButton } from "@/components/CookieBanner";
import { CookieConsentMirror } from "@/components/CookieConsentMirror";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { SSOAgeGate } from "@/components/auth/SSOAgeGate";
import { OnboardingTour } from "@/components/onboarding/OnboardingTour";
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog";
import Index from "./pages/Index";
import { trackPageView } from "@/utils/analytics";
import SharedSection from "./pages/SharedSection";
import { isNative } from "@/utils/platform";
import { useKeyboardOffset } from "@/hooks/useKeyboardOffset";
const DailyQuiz = lazy(() => import("./pages/DailyQuiz"));
const FriendQuiz = lazy(() => import("./pages/FriendQuiz"));
const FriendQuizCreate = lazy(() => import("./pages/FriendQuizCreate"));
const FriendQuizDetail = lazy(() => import("./pages/FriendQuizDetail"));
const FriendQuizResults = lazy(() => import("./pages/FriendQuizResults"));
const FriendQuizTake = lazy(() => import("./pages/FriendQuizTake"));

const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const Friends = lazy(() => import("./pages/Friends"));
const Referrals = lazy(() => import("./pages/Referrals"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const CreateWishlist = lazy(() => import("./pages/CreateWishlist"));
const PublicRandomizer = lazy(() => import("./pages/PublicRandomizer"));
const PublicWishlistShare = lazy(() => import("./pages/PublicWishlistShare"));
const PublicWishlistEdit = lazy(() => import("./pages/PublicWishlistEdit"));
const Settings = lazy(() => import("./pages/Settings"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const AIAssistant = lazy(() => import("./pages/AIAssistant"));
const Profile = lazy(() => import("./pages/Profile"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Impressum = lazy(() => import("./pages/Impressum"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Randomizer = lazy(() => import("./pages/Randomizer"));
const RandomizerLanding = lazy(() => import("./pages/RandomizerLanding"));
const RandomizerInvitation = lazy(() => import("./pages/RandomizerInvitation"));
const Reminders = lazy(() => import("./pages/Reminders"));
const Events = lazy(() => import("./pages/Events"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const EventRSVP = lazy(() => import("./pages/EventRSVP"));
const EventManagementLanding = lazy(() => import("./pages/EventManagementLanding"));
const Games = lazy(() => import("./pages/Games"));
const MobileMenu = lazy(() => import("./pages/MobileMenu"));

// Feature landing pages
const SmartWishlistsLanding = lazy(() => import("./pages/SmartWishlistsLanding"));
const AIGiftAssistantLanding = lazy(() => import("./pages/AIGiftAssistantLanding"));
const SocialGiftingLanding = lazy(() => import("./pages/SocialGiftingLanding"));
const GiveWhatsWantedLanding = lazy(() => import("./pages/GiveWhatsWantedLanding"));
const PriceDropAlertsLanding = lazy(() => import("./pages/PriceDropAlertsLanding"));
const ChromeExtensionLanding = lazy(() => import("./pages/ChromeExtensionLanding"));
const FriendQuizLanding = lazy(() => import("./pages/FriendQuizLanding"));
const MafiaLanding = lazy(() => import("./pages/MafiaLanding"));
const Mafia = lazy(() => import("./pages/Mafia"));
const MafiaCreate = lazy(() => import("./pages/MafiaCreate"));
const MafiaDetail = lazy(() => import("./pages/MafiaDetail"));
const SyncLanding = lazy(() => import("./pages/SyncLanding"));
const Sync = lazy(() => import("./pages/Sync"));
const SyncCreate = lazy(() => import("./pages/SyncCreate"));
const SyncDetail = lazy(() => import("./pages/SyncDetail"));
const SyncJoin = lazy(() => import("./pages/SyncJoin"));

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant"
    });

    trackPageView(pathname);

    const baseTitle = "BuyForS";
    let newTitle = "Smart Gift-Giving Platform - BuyForS";

    switch (true) {
      case pathname === "/":
        newTitle = "Smart Gift-Giving Platform - BuyForS";
        break;
      case pathname === "/login":
        newTitle = "Login - BuyForS";
        break;
      case pathname === "/register":
        newTitle = "Create Account - BuyForS";
        break;
      case pathname === "/forgot-password":
        newTitle = "Forgot Password - BuyForS";
        break;
      case pathname === "/reset-password":
        newTitle = "Reset Password - BuyForS";
        break;
      case pathname === "/verify-email":
        newTitle = "Verify Email - BuyForS";
        break;
      case pathname === "/contact":
        newTitle = "Contact Us - BuyForS";
        break;
      case pathname === "/about":
        newTitle = "About BuyForS";
        break;
      case pathname === "/privacy-policy":
        newTitle = "Privacy Policy - BuyForS";
        break;
      case pathname === "/terms-of-service":
        newTitle = "Terms of Service - BuyForS";
        break;
      case pathname === "/faq":
        newTitle = "FAQ - BuyForS";
        break;
      case pathname === "/impressum":
        newTitle = "Impressum - BuyForS";
        break;
      case pathname === "/create-wishlist":
        newTitle = "Create Wishlist - BuyForS";
        break;
      case pathname === "/friends":
        newTitle = "Friends - BuyForS";
        break;
      case pathname === "/referrals":
        newTitle = "Referrals - BuyForS";
        break;
      case pathname === "/wishlist":
        newTitle = "Your Wishlist - BuyForS";
        break;
      case pathname === "/events":
        newTitle = "Events - BuyForS";
        break;
      case pathname.startsWith("/events/"):
        newTitle = "Event Details - BuyForS";
        break;
      case pathname === "/reminders":
        newTitle = "Reminders - BuyForS";
        break;
      case pathname === "/settings":
        newTitle = "Settings - BuyForS";
        break;
      case pathname === "/ai-assistant":
        newTitle = "AI Assistant - BuyForS";
        break;
      case pathname === "/randomizer":
        newTitle = "Randomizer - BuyForS";
        break;
      case pathname === "/public-randomizer":
        newTitle = "Public Randomizer - BuyForS";
        break;
      case pathname === "/smart-wishlists":
        newTitle = "Smart Wishlists - BuyForS";
        break;
      case pathname === "/ai-gift-assistant":
        newTitle = "AI Gift Assistant - BuyForS";
        break;
      case pathname === "/social-gifting":
        newTitle = "Social Gifting - BuyForS";
        break;
      case pathname === "/give-whats-wanted":
        newTitle = "Give What's Wanted - BuyForS";
        break;
      case pathname === "/price-drop-alerts":
        newTitle = "Price Drop Alerts - BuyForS";
        break;
      case pathname === "/event-management":
        newTitle = "Event Management - BuyForS";
        break;
      case pathname === "/chrome-extension":
        newTitle = "Chrome Extension - BuyForS";
        break;
      case pathname === "/daily-quiz":
        newTitle = "Daily Tiny Quiz - BuyForS";
        break;
      case pathname === "/friend-quiz":
        newTitle = "Friend Quiz - BuyForS";
        break;
      case pathname === "/friend-quiz/create":
        newTitle = "Create Friend Quiz - BuyForS";
        break;
      case pathname.startsWith("/friend-quiz/take/"):
        newTitle = "Take Friend Quiz - BuyForS";
        break;
      case pathname.match(/\/friend-quiz\/\d+\/results/)?.input === pathname:
        newTitle = "Quiz Results - BuyForS";
        break;
      case pathname.match(/\/friend-quiz\/\d+/)?.input === pathname:
        newTitle = "Quiz Details - BuyForS";
        break;
      case pathname === "/friend-quiz-info":
        newTitle = "Friend Quiz - BuyForS";
        break;
      case pathname === "/mafia-info":
        newTitle = "Mafia Game - BuyForS";
        break;
      case pathname === "/mafia":
        newTitle = "Mafia Game - BuyForS";
        break;
      case pathname === "/mafia/create":
        newTitle = "Create Mafia Game - BuyForS";
        break;
      case pathname.match(/\/mafia\/\d+$/)?.input === pathname:
        newTitle = "Mafia Game - BuyForS";
        break;
      case pathname.match(/\/mafia\/\d+\/invite$/)?.input === pathname:
        newTitle = "Invite Players - Mafia Game - BuyForS";
        break;
      case pathname === "/sync-info":
        newTitle = "Sync Game - BuyForS";
        break;
      case pathname === "/sync":
        newTitle = "Sync Game - BuyForS";
        break;
      case pathname === "/sync/create":
        newTitle = "Create Sync Game - BuyForS";
        break;
      case pathname.match(/\/sync\/\d+$/)?.input === pathname:
        newTitle = "Sync Game - BuyForS";
        break;
      case pathname.startsWith("/sync/join/"):
        newTitle = "Join Sync Game - BuyForS";
        break;
      default:
        newTitle = `${baseTitle} | ${pathname.replace("/", "") || "Home"}`;
    }

    document.title = newTitle;
  }, [pathname]);

  return null;
};

const PageSkeleton = () => (
  <div className="min-h-screen bg-[#F1F0FB]/20 dark:bg-gray-900 px-4 pt-[calc(4rem+env(safe-area-inset-top))] pb-24">
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="h-8 w-1/3 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
      <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-32 bg-gray-200 dark:bg-gray-800 rounded-lg animate-pulse"
          />
        ))}
      </div>
    </div>
  </div>
);

const useBottomNavPreload = () => {
  useEffect(() => {
    const run = () => {
      void import("./pages/AIAssistant");
      void import("./pages/Events");
      void import("./pages/Wishlist");
      void import("./pages/Games");
      void import("./pages/MobileMenu");
    };
    const ric = (window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (handle: number) => void;
    }).requestIdleCallback;
    if (ric) {
      const handle = ric(run);
      return () => {
        const cic = (window as Window & {
          cancelIdleCallback?: (handle: number) => void;
        }).cancelIdleCallback;
        cic?.(handle);
      };
    }
    const t = window.setTimeout(run, 1500);
    return () => window.clearTimeout(t);
  }, []);
};

const PENDING_SHARE_URL_KEY = 'bfs_pending_share_url';

const NativeDeepLinkHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNative()) return;

    let listenerHandle: { remove: () => void } | null = null;

    const handleWishlistAddDeepLink = (rawUrl: string | null) => {
      if (!rawUrl) return;
      try {
        const target = `/wishlist?addUrl=${encodeURIComponent(rawUrl)}`;
        sessionStorage.setItem(PENDING_SHARE_URL_KEY, rawUrl);
        sessionStorage.setItem('loginReturnUrl', target);
        navigate(target, { replace: true });
      } catch (e) {
        console.warn('Failed to handle share deep link', e);
      }
    };

    // The shared image itself stays in native storage (too large for a query
    // param); the Wishlist page reads and uploads it once the user is signed in.
    const handleWishlistAddImageDeepLink = () => {
      try {
        const target = '/wishlist?addImage=1';
        sessionStorage.setItem('loginReturnUrl', target);
        navigate(target, { replace: true });
      } catch (e) {
        console.warn('Failed to handle share image deep link', e);
      }
    };

    const checkPendingShare = async () => {
      try {
        const { pendingShare } = await import('@/api/pendingShare');

        const pendingUrl = await pendingShare.getUrl();
        if (pendingUrl) {
          await pendingShare.clearUrl();
          handleWishlistAddDeepLink(pendingUrl);
          return;
        }

        const pendingImage = await pendingShare.getImage();
        if (pendingImage) {
          handleWishlistAddImageDeepLink();
        }
      } catch (e) {
        console.warn('Failed to read pending share', e);
      }
    };

    const setup = async () => {
      const { App: CapApp } = await import('@capacitor/app');
      const { Browser } = await import('@capacitor/browser');
      const { authApi } = await import('@/api/auth');

      listenerHandle = await CapApp.addListener('appUrlOpen', async (data) => {
        try {
          const url = new URL(data.url);

          if (url.protocol === 'https:' && (url.host === 'buyfors.com' || url.host === 'www.buyfors.com')) {
            navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true });
            return;
          }

          if (url.host === 'wishlist' && url.pathname.replace(/^\//, '') === 'add') {
            if (url.searchParams.get('pendingImage')) {
              handleWishlistAddImageDeepLink();
              return;
            }
            const sharedUrl = url.searchParams.get('url');
            handleWishlistAddDeepLink(sharedUrl);
            return;
          }

          const code = url.searchParams.get('code');

          try { await Browser.close(); } catch { /* ignore */ }

          if (!code) {
            return;
          }

          sessionStorage.setItem('ssoInProgress', 'true');
          sessionStorage.removeItem('ssoProcessed');

          try {
            await authApi.exchangeNativeSSOCode(code);
          } catch (e) {
            console.error('SSO exchange failed', e);
            sessionStorage.removeItem('ssoInProgress');
            navigate('/login', { replace: true });
            return;
          }

          const storedReturnUrl = sessionStorage.getItem('loginReturnUrl');
          if (storedReturnUrl) {
            sessionStorage.removeItem('loginReturnUrl');
          }
          const returnUrl = storedReturnUrl || '/';
          sessionStorage.setItem('ssoProcessed', 'true');
          navigate(returnUrl, { replace: true });
        } catch {
          // ignore malformed URLs
        }
      });

      checkPendingShare();
    };

    setup();
    return () => { listenerHandle?.remove(); };
  }, [navigate]);

  return null;
};

function ThemeToaster() {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme}
      position="top-center"
      visibleToasts={3}
      toastOptions={{
        classNames: {
          toast:
            "!rounded-2xl !border !border-border/40 !shadow-[0_10px_40px_-10px_rgba(0,0,0,0.25)] !backdrop-blur-xl !bg-background/90 dark:!bg-background/85 !px-4 !py-3 !gap-3",
          title: "!text-sm !font-semibold !text-foreground",
          description: "!text-xs !text-foreground/70",
          success: "[&_[data-icon]_svg]:!text-emerald-500",
          error: "[&_[data-icon]_svg]:!text-rose-500",
          info: "[&_[data-icon]_svg]:!text-sky-500",
          warning: "[&_[data-icon]_svg]:!text-amber-500",
        },
      }}
    />
  );
}

function CookieButtonManager() {
  const { user } = useAuth();

  if (user) return null;
  if (isNative()) return null;

  return (
    <div className="fixed right-4 bottom-20 z-[9999]">
      <CookieSettingsButton compact={true} />
    </div>
  );
}

function HomeRoute() {
  const { user } = useAuth();
  if (isNative() && !user) {
    return <Navigate to="/login" replace />;
  }
  return <Index />;
}

function KeyboardOffsetMount() {
  useKeyboardOffset();
  return null;
}

function BottomNavPreloadMount() {
  useBottomNavPreload();
  return null;
}

function AppChrome() {
  const { user } = useAuth();
  const { hideNavbar, hideSidebar } = useChromeState();
  const showBottomNav = isNative() && !!user && !hideNavbar;
  return (
    <>
      {!hideNavbar && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <Navbar />
        </div>
      )}
      {!hideSidebar && !!user && <Sidebar />}
      {showBottomNav && <MobileBottomNav />}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <BrowserRouter>
            <KeyboardOffsetMount />
            <BottomNavPreloadMount />
            <NativeDeepLinkHandler />
            <ThemeProvider>
              <LanguageProvider>
                <AuthProvider>
              <ScrollToTop />
              <ThemeToaster />

              <CookieBanner />
              <CookieButtonManager />
              <CookieConsentMirror />

              {/* GDPR Art. 8 + § 8 BDSG: block SSO users until they self-declare 16+. */}
              <SSOAgeGate />

              <OnboardingTour />
              <FeedbackDialog />

              <ChromeProvider>
                <AppChrome />
              <Routes>
                <Route path="/" element={<HomeRoute />} />
                <Route path="*" element={
                  <Suspense fallback={<PageSkeleton />}>
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                      <Route path="/create-wishlist" element={<CreateWishlist />} />
                      <Route path="/public-randomizer" element={<PublicRandomizer />} />

                      <Route path="/public/wishlist/share/:uuid" element={<PublicWishlistShare />} />
                      <Route path="/public/wishlist/edit/:uuid" element={<PublicWishlistEdit />} />

                      <Route path="/friends" element={
                        <ProtectedRoute>
                          <Friends />
                        </ProtectedRoute>
                      } />
                      <Route path="/referrals" element={
                        <ProtectedRoute>
                          <Referrals />
                        </ProtectedRoute>
                      } />
                      <Route path="/wishlist" element={
                        <ProtectedRoute>
                          <Wishlist />
                        </ProtectedRoute>
                      } />
                      <Route path="/settings" element={
                        <ProtectedRoute>
                          <Settings />
                        </ProtectedRoute>
                      } />
                      <Route path="/ai-assistant" element={<AIAssistantRoute />} />
                      <Route path="/reminders" element={
                        <ProtectedRoute>
                          <Reminders />
                        </ProtectedRoute>
                      } />
                      <Route path="/events" element={
                        <ProtectedRoute>
                          <Events />
                        </ProtectedRoute>
                      } />
                      <Route path="/events/:eventId" element={
                        <ProtectedRoute>
                          <EventDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/events/rsvp/:token" element={<EventRSVP />} />
                      <Route path="/randomizer" element={
                        <ProtectedRoute>
                          <Randomizer />
                        </ProtectedRoute>
                      } />
                      <Route path="/games" element={
                        <ProtectedRoute>
                          <Games />
                        </ProtectedRoute>
                      } />
                      <Route path="/menu" element={
                        <ProtectedRoute>
                          <MobileMenu />
                        </ProtectedRoute>
                      } />
                      
                      {/* Feature landing pages */}
                      <Route path="/event-management" element={<EventManagementRoute />} />
                      <Route path="/smart-wishlists" element={<SmartWishlistsRoute />} />
                      <Route path="/ai-gift-assistant" element={<AIGiftAssistantRoute />} />
                      <Route path="/social-gifting" element={<SocialGiftingRoute />} />
                      <Route path="/give-whats-wanted" element={<GiveWhatsWantedRoute />} />
                      <Route path="/price-drop-alerts" element={<PriceDropAlertsRoute />} />
                      <Route path="/chrome-extension" element={<ChromeExtensionRoute />} />
                      <Route path="/randomizer-info" element={<RandomizerLandingRoute />} />
                      <Route path="/friend-quiz-info" element={<FriendQuizLandingRoute />} />
                      <Route path="/mafia-info" element={<MafiaLandingRoute />} />
                      <Route path="/daily-quiz" element={<DailyQuiz />} />
                      
                      {/* Friend Quiz */}
                      <Route path="/friend-quiz" element={
                        <ProtectedRoute>
                          <FriendQuiz />
                        </ProtectedRoute>
                      } />
                      <Route path="/friend-quiz/create" element={
                        <ProtectedRoute>
                          <FriendQuizCreate />
                        </ProtectedRoute>
                      } />
                      <Route path="/friend-quiz/:quizId" element={
                        <ProtectedRoute>
                          <FriendQuizDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/friend-quiz/:quizId/results" element={
                        <ProtectedRoute>
                          <FriendQuizResults />
                        </ProtectedRoute>
                      } />
                      <Route path="/friend-quiz/take/:shareToken" element={
                        <ProtectedRoute>
                          <FriendQuizTake />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/randomizer/accept/:token" element={<RandomizerInvitation />} />
                      
                      {/* Mafia Game */}
                      <Route path="/mafia" element={
                        <ProtectedRoute>
                          <Mafia />
                        </ProtectedRoute>
                      } />
                      <Route path="/mafia/create" element={
                        <ProtectedRoute>
                          <MafiaCreate />
                        </ProtectedRoute>
                      } />
                      <Route path="/mafia/:gameId" element={
                        <ProtectedRoute>
                          <MafiaDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/mafia/:gameId/invite" element={
                        <ProtectedRoute>
                          <MafiaDetail />
                        </ProtectedRoute>
                      } />

                      {/* Sync Game */}
                      <Route path="/sync-info" element={<SyncLandingRoute />} />
                      <Route path="/sync" element={
                        <ProtectedRoute>
                          <Sync />
                        </ProtectedRoute>
                      } />
                      <Route path="/sync/create" element={
                        <ProtectedRoute>
                          <SyncCreate />
                        </ProtectedRoute>
                      } />
                      <Route path="/sync/:gameId" element={
                        <ProtectedRoute>
                          <SyncDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/sync/join/:shareToken" element={
                        <ProtectedRoute>
                          <SyncJoin />
                        </ProtectedRoute>
                      } />

                      <Route path="/profile/:userId" element={<Profile />} />
                      <Route path="/profile/:userId/sections/:sectionId" element={<SharedSection />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/terms-of-service" element={<TermsOfService />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/impressum" element={<Impressum />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                } />
              </Routes>
              </ChromeProvider>
            </AuthProvider>
          </LanguageProvider>
          </ThemeProvider>
        </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

function RandomizerLandingRoute() {
  return <RandomizerLanding />;
}

function FriendQuizLandingRoute() {
  const { user } = useAuth();
  return user ? <Navigate to="/friend-quiz" /> : <FriendQuizLanding />;
}

function MafiaLandingRoute() {
  const { user } = useAuth();
  return user ? <Navigate to="/mafia" /> : <MafiaLanding />;
}

function SyncLandingRoute() {
  const { user } = useAuth();
  return user ? <Navigate to="/sync" /> : <SyncLanding />;
}

function SmartWishlistsRoute() {
  const { user } = useAuth();
  return user ? <Navigate to="/wishlist" /> : <SmartWishlistsLanding />;
}

function AIGiftAssistantRoute() {
  const { user } = useAuth();
  return user ? <Navigate to="/ai-assistant" /> : <AIGiftAssistantLanding />;
}

function SocialGiftingRoute() {
  const { user } = useAuth();
  return user ? <Navigate to="/friends" /> : <SocialGiftingLanding />;
}

function GiveWhatsWantedRoute() {
  const { user } = useAuth();
  return user ? <Navigate to="/wishlist" /> : <GiveWhatsWantedLanding />;
}

function PriceDropAlertsRoute() {
  const { user } = useAuth();
  return user ? <Navigate to="/wishlist" /> : <PriceDropAlertsLanding />;
}

function AIAssistantRoute() {
  const { user } = useAuth();
  return user ? <AIAssistant /> : <AIGiftAssistantLanding />;
}

function EventManagementRoute() {
  const { user } = useAuth();
  return user ? <Navigate to="/events" /> : <EventManagementLanding />;
}

function ChromeExtensionRoute() {
  return <ChromeExtensionLanding />;
}

export default App;
